import { Box, Modal, Stack, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { FC, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import {
  useCameraIdToSid,
  useCreateModerationEvent,
  useLocation,
  useLocationCameras,
  useModerationEvents,
  usePatchModerationEvent
} from '../../../api/apiQueries'
import {
  ActionStatus,
  Camera,
  DateInterval,
  DeviceInfractionDetailsInfractionType,
  DeviceInfractionDetailsType,
  DeviceType,
  ModerationEvent,
  ModerationEventSourceDto
} from '../../../api/models'
import { HttpError } from '../../../api/HttpError'
import { Page } from '../../../components/Page'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { CameraCard } from './CameraCard'
import { CameraHistoryList } from './CameraHistory/CameraHistoryList'
import { DateTimeRange } from './DocumentPanel/DateTimeRange'
import { InterruptionPanel } from './InterruptionPanel'
import { useLocation as useRouterLocation, useNavigate, useParams } from 'react-router-dom'
import { ActionType, CAMERA_OVERVIEW_URL, CAMERA_OVERVIEW_URL_WITH_CAMERA_ID, ModerationEventStatus } from './cameraOverviewEnums'
import { deriveCameraCardBadge, filterOutdoorCameras, getCommunicationBody, getTemplateName } from './cameraOverviewUtils'
import { isOdmonEnrolledCamera } from '../../../utils/utils'
import NoEntriesImage from '../../../assets/images/no-entries.svg?react'
import { DocumentPanel } from './DocumentPanel/DocumentPanel'
import { ConfirmationModal } from './ConfirmationModal'
import { LoggerName, namedLogger } from '../../../utils/logger'
import { ALL_APPLET_NAMES } from '../../../constants'

const logger = namedLogger(LoggerName.CameraOverviewPage)
import { isUuid } from '../../../utils/utils'
import { ErrorScreen } from '../../../components/ErrorScreen'
import { Banner, BannerType } from '../../../components/Banner/Banner'
import { Skeleton } from '../../../components/Skeleton/Skeleton'

export const CameraOverviewPage: FC = () => {
  const navigate = useNavigate()
  const routerLocation = useRouterLocation()
  const { sid: locationId, cameraId } = useParams<{ sid: string; cameraId?: string }>()
  const location = useLocation(locationId)
  const { toast } = useSnackbar()

  const createModerationEvent = useCreateModerationEvent()
  const patchModerationEvent = usePatchModerationEvent()

  const { data: cameras = [], isFetched: areCamerasFetched } = useLocationCameras(locationId!)
  const { data: modEvents = [], isFetched: areModerationEventsFetched } = useModerationEvents(locationId!)

  // Add the camera ID to location ID translation hook
  const getSidFromCameraIdLookup = useCameraIdToSid()

  // Moderation events to show in the camera history
  const events = modEvents.filter(event =>
    [ModerationEventStatus.Active, ModerationEventStatus.Completed, ModerationEventStatus.Failed].includes(event.status)
  )

  const outdoorCameras = useMemo(() => {
    return filterOutdoorCameras(cameras)
  }, [cameras])

  const [activeCameraId, setActiveCameraId] = useState<string | null>(null)
  const [pendingCameraId, setPendingCameraId] = useState<string | null>(null)
  const [pendingClosePanel, setPendingClosePanel] = useState(false)
  const [pendingConfirmationReview, setPendingConfirmationReview] = useState(false)
  const [selectedAction, setSelectedAction] = useState<ActionType>(ActionType.Communication)
  const [selectedReason, setSelectedReason] = useState<DeviceInfractionDetailsInfractionType>(
    DeviceInfractionDetailsInfractionType.ImproperDevicePlacement
  )
  const [specificDates, setSpecificDates] = useState<DateTime[]>([])
  const [dateRange, setDateRange] = useState<DateTimeRange>({ start: null, end: null })
  const [createdEventResponse, setCreatedEventResponse] = useState<ModerationEvent | null>(null)
  const [hasCameraIdSearchError, setHasCameraIdSearchError] = useState(false)
  const lastSyncedRouteCameraId = useRef<string | null>(null)

  const shouldShowCameraIdSearchError =
    hasCameraIdSearchError || Boolean((routerLocation.state as { cameraIdSearchError?: boolean } | null)?.cameraIdSearchError)

  const showDocumentPanel = useMemo(() => activeCameraId !== null, [activeCameraId])
  const showInterruptionPanel = useMemo(() => pendingCameraId !== null, [pendingCameraId])
  const isCameraHistoryEmpty = events.length === 0

  const logPageLoad = useEffectEvent(() => {
    logger.info('Camera Overview Page loaded', {
      context: {
        applet: ALL_APPLET_NAMES.MONITORING_MODERATION,
        locationId: locationId ?? 'unknown',
        cameraId: cameraId ?? 'none'
      }
    })
  })

  useEffect(() => {
    if (location.error) {
      logger.error(location.error instanceof Error ? location.error : new Error(`Failed to fetch location for locationId '${locationId}'`), {
        context: {
          applet: ALL_APPLET_NAMES.MONITORING_MODERATION,
          locationId: locationId ?? 'unknown'
        }
      })
    } else {
      logPageLoad()
    }
  }, [locationId, location.error, logPageLoad])

  useEffect(() => {
    // if no locationId but cameraId is present, make search with cameraId
    if (!locationId && cameraId) {
      getSidFromCameraIdLookup(cameraId).then(translated => {
        if (translated) {
          const locId = translated.locationId
          navigate(CAMERA_OVERVIEW_URL_WITH_CAMERA_ID(locId, cameraId))
        }
      })
    }
  }, [cameraId, getSidFromCameraIdLookup, locationId, navigate])

  /**
   * This useEffect is responsible for setting the activeCameraId from the URL when the component first loads
   * or when the cameraId in the URL changes.
   */
  useEffect(() => {
    if (!cameraId) {
      lastSyncedRouteCameraId.current = null
      return
    }

    // Only hydrate local state from the URL once per camera id.
    // This preserves deep-link/page-refresh behavior without letting later rerenders
    // override an explicit user-driven close.
    if (lastSyncedRouteCameraId.current === cameraId) {
      return
    }

    // Only run if camera ID exists, location is loaded, and camera query has completed
    if (location?.data && areCamerasFetched) {
      if (outdoorCameras.some(cam => cam.uuid === cameraId)) {
        const uuid = cameraId ?? null
        lastSyncedRouteCameraId.current = cameraId
        setHasCameraIdSearchError(false)
        const cam = outdoorCameras.find(c => c.uuid === uuid)
        if (cam && isOdmonEnrolledCamera(cam)) {
          setActiveCameraId(uuid)
        }
      } else {
        lastSyncedRouteCameraId.current = cameraId
        setHasCameraIdSearchError(true)
        setActiveCameraId(null)
        navigate(CAMERA_OVERVIEW_URL(locationId!), { state: { cameraIdSearchError: true } })
      }
    }
  }, [cameraId, events, location?.data, locationId, navigate, outdoorCameras, areCamerasFetched])

  const handleCameraAction = (camera: Camera) => {
    setHasCameraIdSearchError(false)

    // If no camera is active, select the clicked camera
    if (activeCameraId === null) {
      setActiveCameraId(camera.uuid)
      navigate(CAMERA_OVERVIEW_URL_WITH_CAMERA_ID(locationId!, camera.uuid), { state: null })
      return
    }

    // If the same camera is clicked, do nothing
    if (activeCameraId === camera.uuid) {
      return
    }
    // If a different camera is clicked, show interruption panel
    setPendingCameraId(camera.uuid)
  }

  const handleCloseClick = () => {
    setPendingClosePanel(true)
  }

  const handleClosePanelConfirm = (confirmed: boolean) => {
    if (confirmed) {
      setActiveCameraId(null)
      setHasCameraIdSearchError(false)
      // Navigate back to camera overview without specific camera
      navigate(CAMERA_OVERVIEW_URL(locationId!), { state: null })
    }
    setPendingClosePanel(false)
  }

  const handleInterruptionConfirm = (confirmed: boolean) => {
    if (confirmed && pendingCameraId) {
      // Switch to the new camera
      setActiveCameraId(pendingCameraId)
      setHasCameraIdSearchError(false)
      navigate(CAMERA_OVERVIEW_URL_WITH_CAMERA_ID(locationId!, pendingCameraId), { state: null })
    }
    // Reset state regardless of choice
    setPendingCameraId(null)
  }

  /**
   * Handles a change in the specific dates from the document panel, updating state accordingly and resetting the date
   * range.
   *
   * @param dates The new array of specific dates
   */
  const handleSpecificDatesChange = (dates: DateTime[]) => {
    setSpecificDates(dates)

    // Clear the date range when specific dates are selected
    setDateRange({ start: null, end: null })
  }

  /**
   * Handles a change in the date range from the document panel, updating state accordingly and resetting the specific
   * dates.
   *
   * @param range The new date range
   */
  const handleDateRangeChange = (range: DateTimeRange) => {
    setDateRange(range)

    // Clear the specific dates when date range is selected
    setSpecificDates([])
  }

  /**
   * Determine if a camera is the last enrolled outdoor camera on the location.
   *
   * @param cameraId The camera id to check if it's the last enrolled camera
   */
  const isLastEnrolledOutdoorCamera = (cameraId: string) => {
    const specifiedCamera = outdoorCameras.find(cam => cam.uuid === cameraId)
    if (!specifiedCamera || !isOdmonEnrolledCamera(specifiedCamera)) {
      return false
    }

    const enrolledOutdoorCameras = outdoorCameras.filter(camera => isOdmonEnrolledCamera(camera))
    return enrolledOutdoorCameras.length === 1 && enrolledOutdoorCameras[0].uuid === cameraId
  }

  /**
   * Determine which actions should be requested.
   *
   * @param cameraId The camera id
   */
  const determineActionsToRequest = (cameraId: string) => {
    // if selectedAction is Warn Customer, return Communication request
    if (selectedAction === ActionType.Communication) {
      return [{ type: ActionType.Communication }]
    }
    // if selectedAction is Unenroll Camera, and camera is last enrolled, return Communication, Unenroll and Customer Escalation requests
    else if (selectedAction === ActionType.UnenrollCamera && isLastEnrolledOutdoorCamera(cameraId)) {
      return [{ type: ActionType.Communication }, { type: ActionType.UnenrollCamera, cameraId }, { type: ActionType.Escalation }]
    }

    // if selectedAction is Unenroll Camera and selectedReason is Gross Misconduct, return Communication, Unenroll and Customer Escalation requests
    else if (selectedAction === ActionType.UnenrollCamera && selectedReason === DeviceInfractionDetailsInfractionType.GrossMisconduct) {
      return [{ type: ActionType.Communication }, { type: ActionType.UnenrollCamera, cameraId }, { type: ActionType.Escalation }]
    }

    // otherwise, if selectedAction is Unenroll Camera, return Communication and Unenroll requests
    else if (selectedAction === ActionType.UnenrollCamera) {
      return [{ type: ActionType.Communication }, { type: ActionType.UnenrollCamera, cameraId }]
    } else {
      return []
    }
  }

  /**
   * Applicable dates for the moderation event being created, based on either specific dates or a date range.
   */
  const applicableDates: DateInterval[] = useMemo(() => {
    if (specificDates.length > 0) {
      // loop through specific dates and return each as a separate interval
      return specificDates.map(date => ({ start: date.toISODate()!, end: date.toISODate()! }))
    } else if (dateRange.start && dateRange.end) {
      return [{ start: dateRange.start.toISODate()!, end: dateRange.end.toISODate()! }]
    } else {
      return [{ start: '', end: '' }]
    }
  }, [specificDates, dateRange])

  /**
   * Collection of items to display in the confirmation modal for the Incident Dates field, formatted as MM/DD/YYYY.
   */
  const incidentDatesItems: string[] = useMemo(() => {
    if (specificDates.length > 0) {
      return specificDates.map(date => date.toFormat('MM/dd/yyyy'))
    } else if (dateRange.start && dateRange.end) {
      const startDateText = dateRange.start.toFormat('MM/dd/yyyy')
      const endDateText = dateRange.end.toFormat('MM/dd/yyyy')
      return [`${startDateText} - ${endDateText}`]
    } else {
      return []
    }
  }, [specificDates, dateRange])

  // Logic for creating the initial moderation event
  const handleConfirmationSelection = async (camera: Camera, details: string, inputLinks: string[]) => {
    let attempts = 0
    let success = false
    let errorMessage

    // Retry logic
    while (attempts < 3 && !success) {
      try {
        const moderationEventSource = {
          details: {
            type: DeviceInfractionDetailsType.DeviceInfraction,
            description: details,
            device: {
              id: camera.uuid,
              name: camera.name,
              type: DeviceType.Camera
            },
            infractionType: selectedReason,
            applicableDates: applicableDates,
            links: inputLinks
          },
          requestedActions: determineActionsToRequest(camera.uuid)
        } as unknown as ModerationEventSourceDto

        const response = await createModerationEvent.mutateAsync({
          locationId: locationId!,
          moderationEventSource
        })

        setCreatedEventResponse(response)
        success = true
      } catch (err) {
        errorMessage = err
        attempts += 1
        if (attempts < 3) {
          // Add a delay before retrying
          await new Promise(res => setTimeout(res, 1000))
        }
      }
    }

    if (success) {
      setPendingConfirmationReview(true)
    } else {
      // All retries exhausted and the moderation event was never created, so error.
      logger.error(
        `Failed to create moderation event after 3 attempts for locationId '${locationId}' and cameraId '${camera.uuid}'`,
        errorMessage instanceof Error ? errorMessage : new Error(String(errorMessage)),
        {
          context: {
            applet: ALL_APPLET_NAMES.MONITORING_MODERATION,
            locationId: locationId ?? 'unknown',
            cameraId: activeCameraId ?? 'unknown'
          }
        }
      )
      toast('Communication failed to send. Retrying.', { variant: 'error' })
    }
  }

  const handleCloseConfirmation = async (shouldSendCommunication: boolean, eventResponse: ModerationEvent) => {
    if (shouldSendCommunication) {
      let attempts = 0
      let success = false

      // Retry logic for updating moderation event
      while (attempts < 3 && !success) {
        try {
          await patchModerationEvent.mutateAsync(eventResponse)
          success = true

          if (selectedAction === ActionType.Communication) {
            toast('Camera warned, and communication sent.', { variant: 'success' })
          } else if (selectedAction === ActionType.UnenrollCamera) {
            toast('Camera unenrolled, and communication sent.', { variant: 'success' })
          }
        } catch (err) {
          if (err instanceof HttpError && err.body) {
            const body = err.body as ModerationEvent
            if (body.actions?.every(action => action.status === ActionStatus.Completed)) {
              success = true
              if (selectedAction === ActionType.Communication) {
                toast('Camera warned, and communication sent.', { variant: 'success' })
              } else if (selectedAction === ActionType.UnenrollCamera) {
                toast('Camera unenrolled, and communication sent.', { variant: 'success' })
              }
            }
          }

          if (!success) {
            attempts += 1
            const context = {
              applet: ALL_APPLET_NAMES.MONITORING_MODERATION,
              locationId: locationId ?? 'unknown',
              cameraId: activeCameraId ?? 'unknown'
            }
            if (attempts < 3) {
              // The patch failed but we retry, so this attempt is a warn rather than an error.
              logger.error(`Attempt ${attempts} to patch moderation event failed, retrying`, err as Error, { context })
              toast('Communication failed to send. Retrying.', { variant: 'error' })
              await new Promise(res => setTimeout(res, 1000))
            } else {
              // All retries exhausted; communication was never confirmed sent, so error.
              logger.error(
                `Failed to patch moderation event after 3 attempts for locationId '${locationId}' and cameraId '${activeCameraId}'`,
                err instanceof Error ? err : new Error(String(err)),
                { context }
              )
              toast('Communication failed to send after 3 attempts.', { variant: 'error' })
            }
          }
        }
      }

      if (success) {
        // Reset active camera after successful update
        setActiveCameraId(null)
      }
    }

    setPendingConfirmationReview(false)
  }

  const handleSearch = (searchedLocationId: string, cameraId?: string) => {
    if (!cameraId && isUuid(searchedLocationId) && locationId) {
      setHasCameraIdSearchError(true)
      setActiveCameraId(null)
      navigate(CAMERA_OVERVIEW_URL(locationId), { state: { cameraIdSearchError: true } })
      return
    }

    if (!cameraId) {
      setHasCameraIdSearchError(false)
      setActiveCameraId(null)
      navigate(CAMERA_OVERVIEW_URL(searchedLocationId), { state: null })
      return
    }

    // First navigate to the new location to trigger data refetch
    if (location.data?.id !== searchedLocationId) {
      setHasCameraIdSearchError(false)
      navigate(CAMERA_OVERVIEW_URL_WITH_CAMERA_ID(searchedLocationId, cameraId), { state: null })
      return
    }

    // Wait for camera query completion for this location before validating camera id
    if (!areCamerasFetched) {
      setHasCameraIdSearchError(false)
      navigate(CAMERA_OVERVIEW_URL_WITH_CAMERA_ID(searchedLocationId, cameraId), { state: null })
      return
    }

    if (outdoorCameras.some(cam => cam.uuid === cameraId)) {
      setHasCameraIdSearchError(false)
      const cam = outdoorCameras.find(c => c.uuid === cameraId)
      if (cam && isOdmonEnrolledCamera(cam)) {
        setActiveCameraId(cameraId)
      }
      navigate(CAMERA_OVERVIEW_URL_WITH_CAMERA_ID(searchedLocationId, cameraId), { state: null })
    } else {
      setHasCameraIdSearchError(true)
      setActiveCameraId(null)
      navigate(CAMERA_OVERVIEW_URL(searchedLocationId), { state: { cameraIdSearchError: true } })
    }
  }

  const isAccountEligibilityDataLoaded = location.isFetched && areCamerasFetched

  const isEligiblePlan = () => location.data?.monitoring?.serviceStatus?.outdoorMonitoring?.hasEligiblePlan

  const noCamerasEnrolled = () => {
    const enrolledOutdoorCameras = outdoorCameras.filter(camera => isOdmonEnrolledCamera(camera))
    return enrolledOutdoorCameras.length === 0
  }

  const shouldShowBanner = isAccountEligibilityDataLoaded && (!isEligiblePlan() || noCamerasEnrolled())

  const bannerSx = {
    fontSize: 'var(--size-m)',
    fontWeight: 700,
    paddingTop: 'var(--spacing-s)',
    paddingRight: 'var(--spacing-2xs)',
    paddingBottom: 'var(--spacing-s)',
    paddingLeft: 'var(--spacing-s)',
    gap: 'var(--spacing-2xs)',
    alignItems: 'center'
  }

  const bannerSlotProps = {
    icon: { sx: { alignSelf: 'center', marginRight: 0, padding: 'var(--spacing-none)' } }
  }

  const bannerContent = () => {
    if (!isEligiblePlan()) {
      return {
        title: 'Customer not on an eligible plan.',
        'data-testid': 'account-level-banner-ineligible-plan',
        slotProps: bannerSlotProps,
        sx: bannerSx
      }
    }
    if (noCamerasEnrolled()) {
      return {
        title: 'All cameras have been unenrolled.',
        'data-testid': 'account-level-banner-all-unenrolled',
        slotProps: bannerSlotProps,
        sx: bannerSx
      }
    }
    return null
  }

  const bannerProps = bannerContent()

  return (
    <Page title="Camera Overview" shouldAllowCameraIdSearch={true} onSearch={handleSearch}>
      {shouldShowCameraIdSearchError ? (
        <ErrorScreen
          data-testid="error-screen"
          title="Oops, sorry a location with this Camera ID could not be found!"
          message="A refresh may fix this issue."
        />
      ) : (
        <>
          <Stack gap={2}>
            <Typography sx={{ fontSize: 'var(--size-m)', fontWeight: 500 }}>Cameras</Typography>
            {shouldShowBanner && bannerProps ? (
              <Banner type={BannerType.Error} {...bannerProps} />
            ) : (
              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{
                  gap: 'var(--spacing-m)',
                  justifyContent: {
                    xs: 'center',
                    sm: 'flex-start'
                  }
                }}
              >
                {outdoorCameras.map(camera => (
                  <CameraCard
                    key={camera.uuid}
                    cameraName={camera.name}
                    model={camera.model}
                    cameraId={camera.uuid}
                    status={deriveCameraCardBadge(camera, events)}
                    isActive={activeCameraId === camera.uuid}
                    onCameraAction={() => handleCameraAction(camera)}
                  />
                ))}
              </Stack>
            )}
            {showDocumentPanel && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  overflowY: 'auto',
                  zIndex: 1300,
                  boxShadow: `-0.1875rem 0.1875rem 0.25rem 0rem #00000020`
                }}
              >
                <DocumentPanel
                  key={activeCameraId}
                  locationName={location?.data?.name || ''}
                  locationId={locationId!}
                  camera={outdoorCameras.find(camera => camera.uuid === activeCameraId)!}
                  isLoading={createModerationEvent.isPending}
                  onClose={handleCloseClick}
                  onContinue={handleConfirmationSelection}
                  onActionChange={setSelectedAction}
                  onReasonChange={setSelectedReason}
                  onSpecificDatesChange={handleSpecificDatesChange}
                  onDateRangeChange={handleDateRangeChange}
                />
              </Box>
            )}
            <Stack>
              {!areModerationEventsFetched ? (
                <Skeleton isLoading data-testid="camera-history-loading" variant="rounded" sx={{ height: '15.125rem', width: '70.5rem' }} />
              ) : !isCameraHistoryEmpty ? (
                <CameraHistoryList key={locationId} data-testid="camera-history-list" events={events} />
              ) : (
                <Box
                  data-testid="camera-history-empty-state"
                  sx={{
                    backgroundColor: 'var(--bg-surface)',
                    border: '0.0625rem solid',
                    borderRadius: '0.5rem',
                    height: '15.125rem',
                    width: '70.5rem'
                  }}
                >
                  <Stack
                    direction={'column'}
                    sx={{
                      height: '100%',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <NoEntriesImage />
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: 'var(--text-secondary)',
                        textAlign: 'center'
                      }}
                    >
                      No actions have been taken
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Stack>
          <InterruptionPanel
            open={showInterruptionPanel}
            prompt="Switch current camera selection?"
            firstOption="Switch"
            secondOption="Stay Here"
            onClose={() => handleInterruptionConfirm(false)}
            onSelection={handleInterruptionConfirm}
          />
          <InterruptionPanel
            open={pendingClosePanel}
            prompt="Exit Documentation"
            details="All progress will be discarded if you leave now."
            firstOption="Exit"
            secondOption="Stay Here"
            onClose={() => handleClosePanelConfirm(false)}
            onSelection={handleClosePanelConfirm}
          />
          <Modal
            open={pendingConfirmationReview}
            onClose={() => {
              if (createdEventResponse) {
                handleCloseConfirmation(false, createdEventResponse)
              }
            }}
            sx={{
              display: 'grid',
              placeItems: 'center'
            }}
            data-testid="confirmation-modal-overlay"
          >
            <ConfirmationModal
              locationName={location?.data?.name || ''}
              locationId={locationId!}
              cameraName={outdoorCameras.find(camera => camera.uuid === activeCameraId)?.name || ''}
              action={
                selectedAction === ActionType.Communication ? 'Warn Customer' : selectedAction === ActionType.UnenrollCamera ? 'Unenroll Camera' : ''
              }
              reason={selectedReason}
              dates={incidentDatesItems}
              templateName={getTemplateName(createdEventResponse)}
              templateText={getCommunicationBody(createdEventResponse, location?.data?.address?.country)}
              isLoading={patchModerationEvent.isPending}
              onConfirm={shouldSendCommunication => {
                handleCloseConfirmation(shouldSendCommunication, createdEventResponse!)
              }}
              data-testid="confirmation-modal"
            />
          </Modal>
        </>
      )}
    </Page>
  )
}
